import React from "react"
import { useSelector, useDispatch } from "react-redux"

import { APPROVED, YES, WORKFLOW_RERUN, VIEW } from "const"
import { CcLibraryItem, CcComplianceHistory } from "server/models"

import { hasProjectAccess } from "client/utils"
import {
    PRM_CC_DRAFTS,
    StoreType,
    DisplayColumns,

    showCcComplianceColumnSettings,
    setCcComplianceSelectedItemIds,
    setCcCompliancePopupMenuTarget,
    setCcCompliancePopupMenuShow,
    setCcComplianceActive,
} from "client/store"
import {
    UserIcon,
    TableColumn,
    NotAssigned,
    CheckButton,
    SettingsButton,
    TableMenu,
    DraftSaved,
    Empty,
} from "client/components"

import InputSelectAll from "./selectAll"
import Open from "../open"

const Columns = (editAccess: boolean) => {
    const baseRoute = useSelector((state: StoreType) => state.ccCompliance.baseRoute)
    const revisionActiveList = useSelector((state: StoreType) => state.ccCompliance.revisionActiveList)
    const itemStateList = useSelector((state: StoreType) => state.ccCompliance.itemStateList)
    const itemHistoryList = useSelector((state: StoreType) => state.ccCompliance.itemHistoryList)
    const tempAnswerList = useSelector((state: StoreType) => state.ccCompliance.tempAnswerList)
    const selectedItemIds = useSelector((state: StoreType) => state.ccCompliance.selectedItemIds)

    const complianceColumns = useSelector((state: StoreType) => state.system.userSettings.displayColumns.compliance)

    const responseList = useSelector((state: StoreType) => state.ccResponse.responseList)
    const levelList = useSelector((state: StoreType) => state.ccApprovalLevel.levelList)
    const moduleLevelList = useSelector((state: StoreType) => state.ccApprovalLevel.moduleLevelList)
    const levelIds = moduleLevelList[1] || []

    const userList = useSelector((state: StoreType) => state.user.userList)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()


    const displayColumns: DisplayColumns = {
        compliantItem: complianceColumns.compliantItem !== undefined ? complianceColumns.compliantItem : true,
        state: complianceColumns.state !== undefined ? complianceColumns.state : true,
    }
    levelIds.forEach((levelId) => {
        const columnId = `level_${levelId}`
        displayColumns[columnId] = complianceColumns[columnId] !== undefined ? complianceColumns[columnId] : true
        const columnAnswerId = `${columnId}_answer`
        displayColumns[columnAnswerId] = complianceColumns[columnAnswerId] !== undefined ? complianceColumns[columnAnswerId] : true
    })

    const viewDraftsAccess = hasProjectAccess(PRM_CC_DRAFTS, VIEW)

    const selectItem = (itemId: number) => {
        const checked = selectedItemIds.includes(itemId)
        let newSelected: number[] = selectedItemIds.slice()
        if (checked) {
            newSelected = newSelected.filter((id) => id !== itemId)
        } else {
            newSelected.push(itemId)
        }
        dispatch(setCcComplianceSelectedItemIds(newSelected))
    }

    const showColumnSettings = () => {
        dispatch(showCcComplianceColumnSettings(true))
    }

    const showContextMenu = (
        e: React.MouseEvent,
        active: CcLibraryItem,
    ) => {
        e.preventDefault()
        e.stopPropagation()

        dispatch(setCcCompliancePopupMenuTarget(e.currentTarget as HTMLElement))
        dispatch(setCcCompliancePopupMenuShow(true))
        dispatch(setCcComplianceActive(active))
    }

    const onClick = (item: CcLibraryItem) => {
        const activeRevision = revisionActiveList[item.librarySetId]
        if (!activeRevision) return
        Open(item.librarySetId, activeRevision.id, item.id, baseRoute, dispatch)
    }

    const columns: TableColumn[] = [
        {
            field: "setting",
            headerFormatter: () => <SettingsButton onClick={showColumnSettings} />,
            cellMinWidth: "46px",
            cellMaxWidth: "46px",
            formatter: (item: CcLibraryItem) => <TableMenu onClick={(e) => showContextMenu(e, item)} />,
        },
        {
            field: "compliantItem",
            header: lang.compliantItem,
            display: displayColumns.compliantItem,
            cellMinWidth: "350px",
            cellMaxWidth: "500px",
            collapsible: true,
            onClick: (item: CcLibraryItem) => onClick(item),
            formatter: (item: CcLibraryItem) => (
                `${item.publicId} - ${item.compliantItem}`
            ),
        },
    ]

    // add columns for each level
    levelIds.forEach((levelId) => {
        const columnId = `level_${levelId}`
        columns.push({
            field: columnId,
            header: levelList[levelId].name,
            display: displayColumns[columnId],
            cellMinWidth: "150px",
            onClick: (item: CcLibraryItem) => onClick(item),
            formatter: (item: CcLibraryItem) => {
                const itemState = (itemStateList[item.librarySetId] && itemStateList[item.librarySetId][item.id]) || null
                const settings = itemState && itemState.settings
                    ? JSON.parse(itemState.settings)
                    : {}
                const userId = settings[levelId] || null
                const user = userList[userId]
                return user ? <UserIcon user={user} size="sm" /> : <NotAssigned />
            },
        })

        const columnAnswerId = `${columnId}_answer`
        columns.push({
            field: columnAnswerId,
            header: `${levelList[levelId].name} ${lang.answer}`,
            display: displayColumns[columnAnswerId],
            cellMinWidth: "150px",
            onClick: (item: CcLibraryItem) => onClick(item),
            formatter: (item: CcLibraryItem) => {
                const itemHistory = (itemHistoryList[item.librarySetId] && itemHistoryList[item.librarySetId][item.id]) || []
                const itemState = (itemStateList[item.librarySetId] && itemStateList[item.librarySetId][item.id]) || null

                let levelHistory: CcComplianceHistory = null
                for (let i = itemHistory.length - 1; i >= 0; i--) {
                    const history = itemHistory[i]
                    if (history.workflow === WORKFLOW_RERUN) {
                        break
                    }
                    if (!history.systemMessage && history.prevLevelId === levelId) {
                        levelHistory = history
                        break
                    }
                    if (!history.systemMessage && history.parentId === null) { // take answer only from the last branch
                        break
                    }
                }

                if (
                    itemState
                    && itemState.levelId === levelId
                    && (itemState.verifierId === userData.userId || viewDraftsAccess)
                    && tempAnswerList[item.librarySetId]
                    && tempAnswerList[item.librarySetId][item.id]
                    && tempAnswerList[item.librarySetId][item.id][itemState.verifierId]
                ) {
                    return <DraftSaved />
                }
                if (levelHistory) {
                    const response = responseList[levelHistory.responseId] || null
                    return response
                        ? response.response
                        : levelHistory.answer || <Empty />
                }
                return <Empty />
            },
        })
    })

    columns.push({
        field: "state",
        header: lang.state,
        display: displayColumns.state,
        cellMinWidth: "200px",
        style: {
            fontSize: "13px",
        },
        onClick: (item: CcLibraryItem) => onClick(item),
        formatter: (item: CcLibraryItem) => {
            const itemHistory = (itemHistoryList[item.librarySetId] && itemHistoryList[item.librarySetId][item.id]) || []
            let lastHistory: CcComplianceHistory = null

            // get the last history row made by user, not by 'system'
            for (let i = itemHistory.length - 1; i >= 0; i--) {
                const history = itemHistory[i]
                if (history.workflow === WORKFLOW_RERUN) {
                    break
                }
                if (!history.systemMessage) {
                    lastHistory = history
                    break
                }
            }

            const itemState = (itemStateList[item.librarySetId] && itemStateList[item.librarySetId][item.id]) || null
            const level = itemState ? levelList[itemState.levelId] : null
            const verifier = itemState ? userList[itemState.verifierId] : null

            if (lastHistory && lastHistory.nextStatus === APPROVED) {
                const response = responseList[lastHistory.responseId]
                return `${lang.status}: ${response && response.approved === YES ? lang.approved : lang.notApproved}`
            }

            if (level) {
                const firstLevel = level.id === levelIds[0]
                return (
                    <div>
                        <div>
                            {`${level.name}: `}
                            {verifier ? <UserIcon user={verifier} size="sm" /> : <NotAssigned />}
                        </div>
                        <div>
                            {`${lang.status}: ${firstLevel ? lang.open : lang.verification}`}
                        </div>
                    </div>
                )
            }

            if (!level && lastHistory) { // approval level was removed or not set
                return (
                    <div>
                        {`${lang.status}: ${lang.verification}`}
                    </div>
                )
            }

            if (!level && !lastHistory) { // no any data
                return (
                    <div>
                        {`${lang.status}: ${lang.open}`}
                    </div>
                )
            }

            return null
        },
    })

    if (editAccess) {
        columns.unshift({
            field: "select",
            headerFormatter: () => <InputSelectAll />,
            cellWidth: "46px",
            cellMinWidth: "46px",
            formatter: (item: CcLibraryItem) => (
                <CheckButton
                    checked={selectedItemIds.includes(item.id)}
                    padding={false}
                    margin={false}
                    onChange={() => selectItem(item.id)}
                />
            ),
        })
    }

    return { columns, displayColumns }
}
export default Columns
